import { create } from 'zustand';
import { messageService } from '../api/services/messageService';

interface MessageState {
  unreadCount: number;
  isPolling: boolean;
  pollingInterval: ReturnType<typeof setInterval> | null;
  fetchUnreadCount: () => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
  decrementUnreadCount: (amount?: number) => void;
  incrementUnreadCount: (amount?: number) => void;
}

export const useMessageStore = create<MessageState>((set, get) => ({
  unreadCount: 0,
  isPolling: false,
  pollingInterval: null,

  fetchUnreadCount: async () => {
    try {
      const count = await messageService.getUnreadCount();
      set({ unreadCount: count });
    } catch (error) {
      // Silently fail - polling will retry
      console.error('Failed to fetch unread count:', error);
    }
  },

  startPolling: () => {
    const { isPolling, fetchUnreadCount } = get();

    if (isPolling) return;

    // Fetch immediately
    fetchUnreadCount();

    // Set up polling every 30 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);

    set({ isPolling: true, pollingInterval: interval });
  },

  stopPolling: () => {
    const { pollingInterval } = get();

    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    set({ isPolling: false, pollingInterval: null });
  },

  decrementUnreadCount: (amount = 1) => {
    set((state) => ({
      unreadCount: Math.max(0, state.unreadCount - amount),
    }));
  },

  incrementUnreadCount: (amount = 1) => {
    set((state) => ({
      unreadCount: state.unreadCount + amount,
    }));
  },
}));
